import { useEffect, useState } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DataTable } from "@/components/common/DataTable";
import { EntityFormDialog } from "@/components/common/EntityFormDialog";
import { EmptyState } from "@/components/common/States";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useFetch, useLookups } from "@/lib/hooks";
import { errorMessage } from "@/lib/api";

const ALL = "__all__";

function Toolbar({ config, q, setQ, filters, setFilters, extra, onCreate, canWrite }) {
  const t = config.testid;
  return (
    <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={config.search} className="pl-9" data-testid={`${t}-search-input`} />
      </div>
      <div className="flex flex-wrap gap-2">
        {(config.filters || []).map((f) => (
          <Select key={f.name} value={filters[f.name] || ALL} onValueChange={(v) => setFilters((s) => ({ ...s, [f.name]: v === ALL ? undefined : v }))}>
            <SelectTrigger className="w-[170px]" data-testid={`${t}-filter-${f.name}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All {f.label.toLowerCase()}</SelectItem>
              {f.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        ))}
        {extra}
        {canWrite && <Button onClick={onCreate} data-testid={`${t}-create-button`}><Plus className="mr-1.5 h-4 w-4" />New {config.singular.toLowerCase()}</Button>}
      </div>
    </div>
  );
}

export default function ResourcePage({ config, fixedParams, createDefaults, onRowClick, toolbarExtra, extraRowActions, reloadKey }) {
  const { api, can } = useWorkspace();
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [filters, setFilters] = useState({});
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  useEffect(() => { const id = setTimeout(() => setDq(q), 300); return () => clearTimeout(id); }, [q]);
  const filterKey = JSON.stringify(filters);
  useEffect(() => setSelected([]), [q, filterKey]);

  const params = { ...fixedParams, ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)), ...(dq ? { q: dq } : {}) };
  const { data, loading, error, reload } = useFetch(`/${config.path}`, params);
  const refNames = [...new Set([...(config.lookups || []), ...config.fields.filter((f) => f.ref).map((f) => f.ref)])];
  const lookups = useLookups(refNames);
  useEffect(() => { if (reloadKey) reload(); }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (payload) => {
    if (editing?.id) await api.patch(`/${config.path}/${editing.id}`, payload);
    else await api.post(`/${config.path}`, payload);
    toast.success(`${config.singular} ${editing?.id ? "updated" : "created"}`);
    reload();
  };
  const remove = async () => {
    try { await api.delete(`/${config.path}/${deleting.id}`); toast.success(`${config.singular} deleted`); reload(); }
    catch (e) { toast.error(errorMessage(e)); }
    setDeleting(null);
  };
  const bulkRemove = async () => {
    try {
      const { data: r } = await api.post(`/${config.path}/bulk-delete`, { ids: selected });
      if (r.deleted) toast.success(`${r.deleted} ${config.singular.toLowerCase()} record(s) deleted`);
      if (r.skipped.length) toast.warning(`${r.skipped.length} kept because they are still linked: ${r.skipped.slice(0, 3).map((s) => `${s.name} (${s.reason})`).join("; ")}${r.skipped.length > 3 ? "…" : ""}`, { duration: 9000 });
      setSelected([]); reload();
    } catch (e) { toast.error(errorMessage(e)); }
    setBulkOpen(false);
  };
  const bulk = config.bulkDelete && can("delete");
  const rowActions = (row) => [
    ...(extraRowActions ? extraRowActions(row, reload) : []),
    ...(can("write") ? [{ id: "edit", label: "Edit", onClick: () => setEditing(row) }] : []),
    ...(can("delete") ? [{ id: "delete", label: "Delete", danger: true, onClick: () => setDeleting(row) }] : []),
  ];

  return (
    <div className="panel overflow-hidden" data-testid={`${config.testid}-panel`}>
      <Toolbar config={config} q={q} setQ={setQ} filters={filters} setFilters={setFilters} canWrite={can("write")} onCreate={() => setEditing({})}
        extra={<>{toolbarExtra}{bulk && selected.length > 0 && <Button variant="destructive" onClick={() => setBulkOpen(true)} data-testid={`${config.testid}-bulk-delete-button`}><Trash2 className="mr-1.5 h-4 w-4" />Delete selected ({selected.length})</Button>}</>} />
      <DataTable columns={config.columns} rows={data} loading={loading} error={error} onRetry={reload} lookups={lookups}
        selected={selected} onSelect={bulk ? setSelected : undefined}
        onRowClick={onRowClick || (can("write") ? (r) => setEditing(r) : undefined)} rowActions={rowActions} testid={`${config.testid}-table`}
        empty={<EmptyState testid={`${config.testid}-empty`} title={dq || Object.values(filters).some(Boolean) ? "No matching records" : `No ${config.singular.toLowerCase()} records yet`}
          description={dq ? "Try a different search or clear the filters." : "Records you create or import will appear here."} />} />
      <EntityFormDialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)} title={editing?.id ? `Edit ${config.singular.toLowerCase()}` : `New ${config.singular.toLowerCase()}`}
        fields={config.fields} record={editing?.id ? editing : null} defaults={createDefaults} lookups={lookups} onSubmit={save} testid={`${config.testid}-form`} />
      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {config.singular.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>The record is soft-deleted and recorded in the audit trail.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-button" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.length} {config.singular.toLowerCase()} record(s)?</AlertDialogTitle>
            <AlertDialogDescription>Records still linked to contacts, sites, opportunities or tasks are kept and listed. Deleted companies are not re-added by future research runs. Everything is recorded in the audit trail.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={bulkRemove} data-testid="bulk-delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
